import { tabChargeEntriesForReceipt } from '@/utils/tabEntryReceiptItems'
import { taxBreakdownRowLabel } from '@/utils/receiptTaxDisplay'

/**
 * Resolve goods subtotal (ex-tax) and total tax for a tab payment receipt context.
 *
 * @param {object|null|undefined} ctx tab_payment_context / receipt snapshot
 * @returns {{ goodsSubtotalMinor: number, taxTotalMinor: number, taxBreakdown: object[] }}
 */
export function resolveTabPaymentReceiptTaxTotals (ctx) {
  if (!ctx) {
    return { goodsSubtotalMinor: 0, taxTotalMinor: 0, taxBreakdown: [] }
  }

  const entries = tabChargeEntriesForReceipt(ctx.entries)
  const fallbackGoods = entries.reduce((sum, entry) => sum + Number(entry.amount_minor || 0), 0)
  const fallbackTax = entries.reduce((sum, entry) => sum + Number(entry.tax_minor || 0), 0)

  const goodsSubtotalMinor = ctx.goods_subtotal_minor != null
    ? Number(ctx.goods_subtotal_minor) || 0
    : fallbackGoods

  const taxTotalMinor = ctx.tax_total_minor != null
    ? Number(ctx.tax_total_minor) || 0
    : fallbackTax

  const taxBreakdown = Array.isArray(ctx.tax_breakdown) ? ctx.tax_breakdown : []

  return { goodsSubtotalMinor, taxTotalMinor, taxBreakdown }
}

/**
 * Tax lines for tab payment receipt (subtotal is shown separately).
 *
 * @param {object|null|undefined} ctx
 * @param {{ genericTaxLabel?: string }} [options]
 * @returns {Array<{ label: string, amountMinor: number, isDetail?: boolean }>}
 */
export function buildTabPaymentReceiptTaxLines (ctx, options = {}) {
  const genericTaxLabel = options.genericTaxLabel || 'Tax'
  const { taxTotalMinor, taxBreakdown } = resolveTabPaymentReceiptTaxTotals(ctx)

  if (taxTotalMinor <= 0 && taxBreakdown.length === 0) {
    return []
  }

  const activeRows = taxBreakdown.filter((row) => Number(row?.amount || 0) > 0)
  const totalAmount = taxTotalMinor / 100

  if (activeRows.length === 1) {
    const row = activeRows[0]
    return [{
      label: taxBreakdownRowLabel(row, genericTaxLabel),
      amountMinor: Math.round((Number(row.amount) || totalAmount) * 100)
    }]
  }

  if (activeRows.length > 1) {
    return [
      { label: genericTaxLabel, amountMinor: taxTotalMinor },
      ...activeRows.map((row) => ({
        label: taxBreakdownRowLabel(row, genericTaxLabel),
        amountMinor: Math.round((Number(row.amount) || 0) * 100),
        isDetail: true
      }))
    ]
  }

  return [{ label: genericTaxLabel, amountMinor: taxTotalMinor }]
}

/**
 * Whether the tab payment receipt should show goods subtotal / tax rows.
 *
 * @param {object|null|undefined} ctx
 */
export function shouldShowTabPaymentReceiptTaxBreakdown (ctx) {
  const { taxTotalMinor } = resolveTabPaymentReceiptTaxTotals(ctx)
  return taxTotalMinor > 0
}
